import { useEffect, useContext, useState, useMemo } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { History } from 'history'
import qs from 'qs'

import { RoutePath } from 'lib/types'
import { localStorage } from 'lib/core/services/localStorage'
import { CoreContext } from 'lib/core/resources/context'
import { SLASH, ZERO, LIVBRITE_DEFAULT_STORE_VIEW } from 'lib/core/resources/constants'
import { usePath, useStoreConfig } from 'lib/core/hooks'
import { checkSession } from 'lib/core/data/auth/session'
import { isFetchingSelector } from 'lib/core/data/common/selectors'
import { getIsInitialized, getIsLoggedIn } from 'lib/core/data/auth/session/selectors'
import { fetchCart } from 'lib/core/data/quote/cart'
import { fetchCategoryList } from 'lib/core/data/common/categoryList'
import { fetchPageList } from 'lib/core/data/common/pageList'
import { fetchProductAttributeList } from 'lib/core/data/common/productAttributeList'
import { fetchStoreConfig } from 'lib/core/data/common/storeConfig'
import { fetchCmsBlocks } from 'lib/core/data/cms/cmsBlock'
import { getCartId } from 'lib/core/data/quote/cartId/selectors'
import { fetchCartId } from 'lib/core/data/quote/cartId'

export const useMain = () => {
  const context = useContext(CoreContext)
  const cartId = useSelector(getCartId)
  const dispatch = useDispatch()
  const history = useHistory()
  const { pathname, search } = useLocation()
  const path = usePath()
  const isLoggedIn = useSelector(getIsLoggedIn)
  const isInitialized = useSelector(getIsInitialized)
  const [storeView] = useState(localStorage.get('storeView'))
  const { isError } = useStoreConfig()
  const storeConfigError = isError || process.env.REACT_APP_MAGENTO_STORE_VIEW === LIVBRITE_DEFAULT_STORE_VIEW || process.env.REACT_APP_ENABLE_MAINTENANCE_MODE === 'true'
  const { storeView: storeViewParam } = qs.parse(search.substring(1))
  const cmsBlocks = context.cmsBlocks
  const isFetching = useSelector(isFetchingSelector)

  const { handleStoreView, shouldSwitchStore } = useMemo(() => {
    if (typeof storeViewParam !== 'string') {
      return {
        shouldSwitchStore: false,
        handleStoreView: () => undefined
      }
    }

    return storeViewHandler({
      localStorage,
      storeViewParam,
      storeView,
      history
    })
  }, [
    storeViewParam,
    storeView,
    history
  ])

  useEffect(() => {
    if (cmsBlocks) {
      dispatch(fetchCmsBlocks(Object.values(cmsBlocks)))
    }
    dispatch(checkSession())
    dispatch(fetchProductAttributeList())
    dispatch(fetchCategoryList())
    dispatch(fetchPageList())
    dispatch(fetchStoreConfig())
  }, [
    dispatch,
    cmsBlocks
  ])

  useEffect(handleStoreView, [handleStoreView])

  useEffect(() => {
    window.scrollTo(ZERO, ZERO)
  }, [
    pathname
  ])

  useEffect(() => {
    if (isInitialized && cartId) {
      dispatch(fetchCart(cartId))
    } else if (isInitialized) {
      dispatch(fetchCartId())
    }
  }, [
    isInitialized,
    cartId,
    dispatch
  ])

  useEffect(() => {
    if (isLoggedIn && isPathnameForbidden(path, pathname)) {
      history.replace(SLASH)
    }
  }, [
    path,
    history,
    pathname,
    isLoggedIn
  ])

  const isLoading = isFetching || !isInitialized || shouldSwitchStore

  return {
    isLoading,
    storeConfigError
  }
}

const isPathnameForbidden = (path: RoutePath, pathname: string): boolean => {
  const forbiddenPathsWhileLoggedIn = [
    path.createAccount,
    path.login,
    path.createAccount,
    path.forgotPassword,
    path.resetPassword
  ]

  return !!forbiddenPathsWhileLoggedIn.find(path => pathname.indexOf(path) === ZERO)
}

interface StoreViewHandler {
  history: History<unknown>
  storeView: string
  storeViewParam: string
  localStorage: typeof localStorage
}

const storeViewHandler = ({ history, storeView, storeViewParam, localStorage }: StoreViewHandler) => {
  const { origin, pathname } = window.location
  const shouldSwitchStore = storeViewParam && storeViewParam !== storeView

  const queryString = qs.stringify({
    ...qs.parse(window.location.search.substring(1)),
    storeView: undefined
  })

  const pathnameWithQueryString = `${pathname}${queryString ? `?${queryString}` : ''}`

  return {
    shouldSwitchStore,
    handleStoreView: () => {
      if (shouldSwitchStore) {
        localStorage.set('storeView', storeViewParam)
        window.location.href = `${origin}${pathnameWithQueryString}`
      } else {
        history.replace(pathnameWithQueryString)
      }
    }
  }
}
